import { useState, useMemo, useEffect } from "react";
import { Box, Typography, Paper, Grid, keyframes } from "@mui/material";
import { StoreOutlined, PeopleOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Layout from "../components/layout/Layout";
import DateNavigator from "../components/common/DateNavigator";
import { useDashboard } from "../hooks/useDashboard";
import {
  TodayOverviewCard,
  EmployeeStatisticsCard,
  ScheduleHealthCard,
  TodayStaffCard,
  ShopPerformanceCard,
  DashboardSkeleton,
} from "../components/dashboard";
import { formatDate, getWeekStart, getWeekDates } from "../utils/scheduleHelpers";
import { useAuth } from "../context/AuthContext";
import OwnerOnboardingModal from "../components/onboarding/OwnerOnboardingModal";
import StaffOnboardingModal from "../components/onboarding/StaffOnboardingModal";

type ViewMode = "daily" | "weekly";

// Pulse animation for post-onboarding highlight
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`;

interface EmptyDashboardProps {
  highlightShopCard?: boolean;
  hasShops: boolean;
  hasEmployees: boolean;
}

function EmptyDashboard({ highlightShopCard = false, hasShops, hasEmployees }: EmptyDashboardProps) {
  const navigate = useNavigate();

  // Determine what's missing to show the right message and cards
  const showShopCard = !hasShops;
  const showEmployeeCard = !hasEmployees;

  const message = !hasShops && !hasEmployees
    ? "Get started by setting up your shops and adding employees. Once you have data, your dashboard will show key metrics and insights."
    : !hasShops
    ? "Add a shop to start creating schedules for your employees."
    : "Add employees to start assigning shifts and managing schedules.";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
        textAlign: "center",
        py: 6,
      }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Welcome to ShiftGrid
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 500, mb: 4 }}
      >
        {message}
      </Typography>

      <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 600 }}>
        {showShopCard && (
          <Grid item xs={12} sm={showEmployeeCard ? 6 : 8}>
            <Paper
              sx={{
                p: 3,
                cursor: "pointer",
                border: "2px solid",
                borderColor: highlightShopCard ? "primary.main" : "divider",
                borderStyle: highlightShopCard ? "solid" : "dashed",
                transition: "all 0.2s",
                animation: highlightShopCard ? `${pulseAnimation} 2s ease-in-out 3` : "none",
                bgcolor: highlightShopCard ? "primary.50" : "background.paper",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "primary.50",
                  transform: "translateY(-4px)",
                },
              }}
              onClick={() => navigate("/shops/information")}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: highlightShopCard ? "primary.200" : "primary.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <StoreOutlined sx={{ fontSize: 28, color: "primary.main" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Add Your First Shop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure shop details and operating hours
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        {showEmployeeCard && (
          <Grid item xs={12} sm={showShopCard ? 6 : 8}>
            <Paper
              sx={{
                p: 3,
                cursor: "pointer",
                border: "2px dashed",
                borderColor: "divider",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "primary.50",
                  transform: "translateY(-4px)",
                },
              }}
              onClick={() => navigate("/employees/information")}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "secondary.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PeopleOutlined sx={{ fontSize: 28, color: "secondary.main" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Add Employees
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create employee profiles and assign to shops
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isLoading,
    hasData,
    hasShops,
    hasEmployees,
    quickActions,
    availabilityMode,
    rawData,
  } = useDashboard();

  // Shared view state for all dashboard cards
  const today = useMemo(() => formatDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMode, setViewMode] = useState<ViewMode>("daily");

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingType, setOnboardingType] = useState<'owner' | 'staff' | null>(null);
  const [showPostOnboardingHighlight, setShowPostOnboardingHighlight] = useState(false);

  // Determine if onboarding should be shown
  useEffect(() => {
    if (!user || isLoading) return;

    // Skip if onboarding already completed
    if (user.onboardingCompleted) return;

    // Determine onboarding type based on role
    if (user.role === 'owner') {
      setOnboardingType('owner');
      setShowOnboarding(true);
    } else if (user.role === 'admin' || user.role === 'manager' || user.role === 'viewer') {
      setOnboardingType('staff');
      setShowOnboarding(true);
    }
  }, [user, isLoading]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);

    // Show post-onboarding guidance based on role and data state
    if (onboardingType === 'owner') {
      if (!hasData) {
        // Owner with no data - highlight the "Add Your First Shop" card
        setShowPostOnboardingHighlight(true);
        enqueueSnackbar("Let's start by adding your first shop!", {
          variant: "info",
          autoHideDuration: 5000,
        });
        // Remove highlight after animation completes (3 pulses × 2s = 6s)
        setTimeout(() => setShowPostOnboardingHighlight(false), 6000);
      } else {
        // Owner with existing data
        enqueueSnackbar("You're all set! Your dashboard is ready.", {
          variant: "success",
          autoHideDuration: 4000,
        });
      }
    } else if (onboardingType === 'staff') {
      // Staff guidance based on role
      const roleGuidance: Record<string, { message: string; action?: () => void }> = {
        admin: {
          message: "You have full access. Explore the dashboard or manage employees.",
        },
        manager: {
          message: "Check out the schedules to manage your team's shifts.",
          action: () => navigate("/schedules/shops"),
        },
        viewer: {
          message: "You can view schedules and employee information.",
        },
      };

      const guidance = roleGuidance[user?.role || 'viewer'] || roleGuidance.viewer;
      enqueueSnackbar(guidance.message, {
        variant: "info",
        autoHideDuration: 5000,
      });
    }
  };

  // Calculate the date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === "daily") {
      return { dates: [selectedDate], label: "day" };
    } else {
      const weekStart = getWeekStart(new Date(selectedDate + "T00:00:00"));
      const weekDates = getWeekDates(weekStart);
      return { dates: weekDates.map(formatDate), label: "week" };
    }
  }, [viewMode, selectedDate]);

  return (
    <Layout pageTitle="Dashboard">
      <Box>
        {isLoading ? (
          <DashboardSkeleton />
        ) : !hasData ? (
          <EmptyDashboard
            highlightShopCard={showPostOnboardingHighlight}
            hasShops={hasShops}
            hasEmployees={hasEmployees}
          />
        ) : (
          <>
            {/* Date Navigation Header */}
            <DateNavigator
              mode="both"
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Overview Stats */}
            <Box sx={{ mb: 3 }}>
              <TodayOverviewCard
                rawData={rawData}
                viewMode={viewMode}
                selectedDate={selectedDate}
              />
            </Box>

            {/* Employee Statistics and Schedule Health */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <EmployeeStatisticsCard
                  rawData={rawData}
                  viewMode={viewMode}
                  dateRange={dateRange.dates}
                  missingAvailabilityAction={quickActions.find(a => a.id === 'missing-availability')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ScheduleHealthCard
                  rawData={rawData}
                  viewMode={viewMode}
                  dateRange={dateRange.dates}
                  availabilityMode={availabilityMode}
                  unfilledShiftsAction={quickActions.find(a => a.id === 'unfilled-shifts')}
                />
              </Grid>
            </Grid>

            {/* Today's Staff */}
            <Box sx={{ mb: 3 }}>
              <TodayStaffCard
                rawData={rawData}
                viewMode={viewMode}
                dateRange={dateRange.dates}
              />
            </Box>

            {/* Shop Performance */}
            <ShopPerformanceCard
              rawData={rawData}
              viewMode={viewMode}
              dateRange={dateRange.dates}
            />
          </>
        )}
      </Box>

      {/* Onboarding Modals */}
      {onboardingType === 'owner' && (
        <OwnerOnboardingModal
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
      {onboardingType === 'staff' && (
        <StaffOnboardingModal
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
    </Layout>
  );
}
